import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, FileText, Shield, Zap, Check, Star, Quote, Upload, Search, RefreshCw, Download } from "lucide-react";
import DemoWidget from "../components/DemoWidget";

const HERO = "https://images.pexels.com/photos/29333569/pexels-photo-29333569.jpeg";
const TECH = "https://images.unsplash.com/photo-1762279389042-9439bfb6c155";

const Steps = [
  { i: Upload, t: "Upload Your File", d: "Drop a PDF, DOCX, PPTX, TXT, or image (JPG/PNG). Up to 50,000 words." },
  { i: Search, t: "Detect AI Content", d: "Sentence-level scan with AI + plagiarism scores." },
  { i: RefreshCw, t: "Paraphrase Flagged", d: "Only AI-flagged sentences get rewritten." },
  { i: Download, t: "Download Clean File", d: "Export to original format, 100% human." },
];

export default function Home() {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/[0.06]">
        <div className="absolute inset-0 grid-bg opacity-40"/>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25"/>
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background"/>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs uppercase tracking-widest text-zinc-300 mb-6">
            <Star size={12} className="text-amber-400"/> AI to Human in under 60 seconds
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold tracking-tighter max-w-4xl leading-[1.05]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Make AI-written text<br/>read like a <span className="text-gradient">real human</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-zinc-400 leading-relaxed">
            Drop a 30-page report, we scan every sentence, paraphrase only what's flagged, and re-check until you hit 0% AI. Submit fast, sound human.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-start-free-btn">
              <Button size="lg" className="bg-indigo-600 hover:bg-indigo-700 glow-primary px-7 h-12 rounded-xl">
                Start Free <ArrowRight size={16} className="ml-1"/>
              </Button>
            </Link>
            <Link to="/pricing" data-testid="hero-pricing-btn">
              <Button variant="outline" size="lg" className="px-7 h-12 rounded-xl glass">View Pricing</Button>
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-3 gap-2 sm:gap-4 max-w-2xl">
            {[
              { v: "7", l: "File formats supported" },
              { v: "1–3", l: "Rounds to 0% AI" },
              { v: "3", l: "Plans — Free to Business" },
            ].map((s, i) => (
              <div key={i} className="glass rounded-xl px-3 sm:px-4 py-3 sm:py-4">
                <div className="text-xl sm:text-2xl font-bold" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{s.v}</div>
                <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-zinc-400">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Live demo — pre-login conversion funnel */}
      <section className="border-b border-white/[0.06] bg-white/[0.01]">
        <DemoWidget />
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">How it works</div>
        <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>From upload to undetectable.</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {Steps.map(({ i: Icon, t, d }, idx) => (
            <div key={idx} className="glass rounded-2xl p-6 hover:bg-white/[0.05] transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center">
                  <Icon size={18} className="text-indigo-300"/>
                </div>
                <div className="text-xs text-zinc-500 font-mono">0{idx+1}</div>
              </div>
              <div className="mt-4 font-semibold">{t}</div>
              <div className="mt-1 text-sm text-zinc-400">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="border-y border-white/[0.06] bg-white/[0.01] relative">
        <div className="absolute inset-0 opacity-30">
          <img src={TECH} alt="" className="w-full h-full object-cover opacity-30"/>
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 to-background"/>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Built for writers under deadline.
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { i: Zap, t: "Sentence-level surgery", d: "We don't rewrite the whole doc — only the parts AI detection flags." },
              { i: RefreshCw, t: "Iterative rounds", d: "Auto re-scan after every paraphrase. Stop when you hit 0%." },
              { i: Shield, t: "Plagiarism aware", d: "AI + plagiarism check in one report, with confidence per sentence." },
              { i: FileText, t: "All major formats", d: "PDF, DOCX, PPTX, TXT — keep the layout, swap the words." },
              { i: Star, t: "Premium readability", d: "Tone-preserving paraphrasing using state-of-the-art LLMs." },
              { i: Check, t: "Plan-aware UX", d: "See exactly which round you're on and what your plan allows." },
            ].map(({ i: Icon, t, d }, idx) => (
              <div key={idx} className="glass rounded-2xl p-6">
                <Icon size={20} className="text-cyan-300"/>
                <div className="mt-3 font-semibold">{t}</div>
                <div className="mt-1 text-sm text-zinc-400">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Pricing</div>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Simple, transparent plans.</h2>
          </div>
          <Link to="/pricing"><Button variant="outline" className="glass">See full pricing →</Button></Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-5">
          {[
            { n: "Free", p: "$0", x: ["1 scan / day", "500 words", "Watermarked"] },
            { n: "Pro", p: "$9.99", x: ["Unlimited scans", "10,000 words", "No watermark"], popular: true },
            { n: "Business", p: "$24.99", x: ["50,000 words", "Batch x10", "API access"] },
          ].map((c, i) => (
            <div key={i} className={`relative rounded-2xl p-6 ${c.popular ? "bg-gradient-to-br from-indigo-600/15 to-purple-600/10 border border-indigo-500/30" : "glass"}`}>
              {c.popular && <div className="absolute -top-3 left-6 text-[10px] tracking-widest uppercase bg-indigo-600 px-2 py-1 rounded-full">Most Popular</div>}
              <div className="text-xs uppercase tracking-widest text-zinc-400">{c.n}</div>
              <div className="mt-1 text-3xl font-bold">{c.p}<span className="text-sm text-zinc-500">/mo</span></div>
              <ul className="mt-4 space-y-1.5 text-sm">
                {c.x.map((y, j) => <li key={j} className="flex gap-2"><Check size={14} className="text-emerald-400 mt-0.5"/>{y}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pb-24">
        <div className="relative overflow-hidden rounded-3xl p-10 lg:p-16 bg-gradient-to-br from-indigo-700/30 via-indigo-600/20 to-cyan-500/15 border border-indigo-500/30">
          <h3 className="text-3xl lg:text-5xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Hit submit. Sound human.
          </h3>
          <p className="mt-3 text-zinc-300 max-w-xl">Stop paraphrasing paragraph by paragraph. Drop your report and get it back clean.</p>
          <Link to="/register" className="inline-block mt-6">
            <Button size="lg" className="bg-white text-indigo-700 hover:bg-zinc-100 px-7 h-12 rounded-xl">Start free now <ArrowRight size={16} className="ml-1"/></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
